import { useState, useEffect } from "react";
import { HelpCircle, Phone, Building2, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportRequestDialog({ user, triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    message: "",
    wants_callback: false,
    callback_phone: ""
  });

  useEffect(() => {
    if (open) {
      fetchUserData();
      setSubmitted(false);
    }
  }, [open]);

  const fetchUserData = async () => {
    try {
      const res = await authFetch(`${API}/auth/me`, { });
      if (res.ok) {
        const data = await res.json();
        setUserData(data);
        // Pre-fill callback phone with user's phone
        if (data.phone) {
          setFormData(prev => ({ ...prev, callback_phone: data.phone }));
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error("Inserisci un messaggio");
      return;
    }
    
    setLoading(true);
    try {
      const res = await authFetch(`${API}/support-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: formData.message,
          wants_callback: formData.wants_callback,
          callback_phone: formData.wants_callback ? formData.callback_phone : ""
        })
      });
      
      if (res.ok) {
        setSubmitted(true);
        toast.success("Richiesta di assistenza inviata!");
        // Reset form after delay
        setTimeout(() => {
          setFormData({ message: "", wants_callback: false, callback_phone: userData?.phone || "" });
        }, 2000);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nell'invio della richiesta");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const handleCallbackChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      wants_callback: checked,
      callback_phone: checked ? (userData?.phone || prev.callback_phone) : prev.callback_phone
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className={`border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white ${triggerClassName}`}
          data-testid="support-btn"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          Assistenza
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Richiesta Assistenza
          </DialogTitle>
        </DialogHeader>
        
        {submitted ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Richiesta Inviata!</h3>
            <p className="text-zinc-400">
              La tua richiesta è stata inviata con successo. 
              Ti risponderemo il prima possibile.
            </p>
            <Button 
              onClick={() => setOpen(false)} 
              className="btn-primary mt-6"
            >
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* User Info Preview */}
            {userData && (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 space-y-2">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">I tuoi dati</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Building2 className="w-4 h-4 text-zinc-500" />
                    <span className="truncate">{userData.company_name || userData.name || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Mail className="w-4 h-4 text-zinc-500" />
                    <span className="truncate">{userData.email || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Phone className="w-4 h-4 text-zinc-500" />
                    <span>{userData.phone || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <MapPin className="w-4 h-4 text-zinc-500" />
                    <span className="truncate">{userData.city || userData.address || "-"}</span>
                  </div>
                </div>
                {userData.vat_number && (
                  <p className="text-xs text-zinc-500 mt-2">P.IVA: {userData.vat_number}</p>
                )}
              </div>
            )}

            {/* Message */}
            <div className="form-group">
              <label className="form-label">Descrivi la tua richiesta *</label>
              <textarea
                className="form-textarea h-32"
                placeholder="Scrivi qui la tua richiesta di assistenza..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                data-testid="support-message"
              />
            </div>

            {/* Callback Option */}
            <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="wants_callback"
                  checked={formData.wants_callback}
                  onCheckedChange={handleCallbackChange}
                  className="mt-1"
                  data-testid="wants-callback"
                />
                <div className="flex-1">
                  <label 
                    htmlFor="wants_callback" 
                    className="text-white font-medium cursor-pointer"
                  >
                    Voglio essere ricontattato
                  </label>
                  <p className="text-zinc-500 text-sm mt-1">
                    Seleziona questa opzione se desideri essere ricontattato telefonicamente
                  </p>
                </div>
              </div>
              
              {formData.wants_callback && (
                <div className="mt-3 pl-7">
                  <label className="form-label text-sm">Numero di telefono</label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="Il tuo numero di telefono"
                      value={formData.callback_phone}
                      onChange={(e) => setFormData({ ...formData, callback_phone: e.target.value })}
                      data-testid="callback-phone"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setOpen(false)}
                className="text-zinc-400"
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={loading}
                data-testid="submit-support"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Invio...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Invia Richiesta
                  </span>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
